import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

/**
 * Desktop: shadcn's muted pill list (theme radius). Below 768px: underlined
 * list in full width (AD-016, mobile layer only with `max-md:`).
 */
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
      'max-md:flex max-md:h-auto max-md:w-full max-md:justify-start max-md:rounded-none max-md:border-b max-md:bg-transparent max-md:p-0',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      'max-md:-mb-px max-md:flex-1 max-md:rounded-none max-md:border-b-2 max-md:border-transparent max-md:px-2 max-md:data-[state=active]:border-primary max-md:data-[state=active]:bg-transparent max-md:data-[state=active]:shadow-none',
      mobileLayer.touch,
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      // `forceMount` keeps inactive panels in the DOM; Radix only sets
      // `hidden` for unmounted ones, so hide them here.
      'data-[state=inactive]:hidden',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
